import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { PageHeader, FormField, LoadingSpinner } from '../../components/UI';
import { applyTheme, loadSettings } from '../../hooks/useAppSettings';
import toast from 'react-hot-toast';

export default function ThemeSettings() {
  const [settings, setSettings] = useState({
    app_name: '',
    header_color: '#1a365d',
    footer_color: '#2d3748',
    primary_color: '#2b6cb0',
    bg_color: '#f7fafc',
    footer_text: '',
    support_email: '',
    support_phone: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get('/settings').then(r => {
      if (r.settings) {
        const s = {
          app_name:      r.settings.app_name      || 'Government NOC Management System',
          header_color:  r.settings.header_color  || '#1a365d',
          footer_color:  r.settings.footer_color  || '#2d3748',
          primary_color: r.settings.primary_color || '#2b6cb0',
          bg_color:      r.settings.bg_color      || '#f7fafc',
          footer_text:   r.settings.footer_text   || '',
          support_email: r.settings.support_email || '',
          support_phone: r.settings.support_phone || '',
        };
        setSettings(s);
        applyTheme(s); // Apply saved theme on load
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const f = (k, v) => {
    setSettings(p => ({ ...p, [k]: v }));
    // Live preview for colors
    if (['header_color','footer_color','primary_color','bg_color'].includes(k)) {
      applyTheme({ ...settings, [k]: v });
    }
    if (k === 'app_name' && v) document.title = v;
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBannerFile(file);
    setBannerPreview(URL.createObjectURL(file));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();

      // Append each setting individually
      Object.entries(settings).forEach(([k, v]) => {
        fd.append(k, v || '');
      });

      if (logoFile)   fd.append('app_logo', logoFile);
      if (bannerFile) fd.append('app_banner', bannerFile);

      const res = await api.post('/settings', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.success) {
        applyTheme(settings);
        applyTheme(settings); // Apply immediately
        await loadSettings(); // Refresh all listeners
        toast.success('Theme settings saved successfully!');
      }
    } catch (err) {
      toast.error('Save failed: ' + (err?.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner size="lg" className="py-20" />;

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="🎨 Theme & Appearance Settings"
        subtitle="Customize application name, colors, logo and branding"
      />

      <form onSubmit={handleSave} className="space-y-6">

        {/* ── App Name ── */}
        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-4">📌 Application Branding</h3>
          <div className="space-y-4">
            <FormField label="Application Name">
              <input
                className="form-input"
                value={settings.app_name}
                onChange={e => f('app_name', e.target.value)}
                placeholder="Government NOC Management System"
              />
              <p className="text-xs text-gray-400 mt-1">
                This name appears in the browser tab and header
              </p>
            </FormField>

            <FormField label="Footer Text">
              <input
                className="form-input"
                value={settings.footer_text}
                onChange={e => f('footer_text', e.target.value)}
                placeholder="© 2024 Government NOC Management System"
              />
            </FormField>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Support Email">
                <input
                  type="email"
                  className="form-input"
                  value={settings.support_email}
                  onChange={e => f('support_email', e.target.value)}
                  placeholder="support@govnoc.gov.in"
                />
              </FormField>
              <FormField label="Support Phone">
                <input
                  className="form-input"
                  value={settings.support_phone}
                  onChange={e => f('support_phone', e.target.value)}
                  placeholder="1800-XXX-XXXX"
                />
              </FormField>
            </div>
          </div>
        </div>

        {/* ── Colors ── */}
        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-1">🎨 Color Theme</h3>
          <p className="text-xs text-gray-400 mb-4">Colors change live as you pick them</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { key: 'header_color',  label: 'Header Background' },
              { key: 'footer_color',  label: 'Footer Background' },
              { key: 'primary_color', label: 'Primary / Button Color' },
              { key: 'bg_color',      label: 'Page Background' },
            ].map(({ key, label }) => (
              <FormField key={key} label={label}>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={settings[key] || '#000000'}
                    onChange={e => f(key, e.target.value)}
                    className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer p-0.5 flex-shrink-0"
                  />
                  <input
                    className="form-input flex-1 font-mono text-sm"
                    value={settings[key] || ''}
                    onChange={e => f(key, e.target.value)}
                    placeholder="#000000"
                    maxLength={7}
                  />
                </div>
              </FormField>
            ))}
          </div>
        </div>

        {/* ── Logo & Banner ── */}
        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-4">🖼️ Logo & Banner</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="form-label">App Logo</label>
              <input
                type="file"
                accept="image/*"
                className="form-input"
                onChange={handleLogoChange}
              />
              <p className="text-xs text-gray-400 mt-1">Recommended: 200×60px PNG</p>
              {logoPreview && (
                <div className="mt-2 p-2 bg-gray-50 border rounded-lg">
                  <img src={logoPreview} alt="Logo preview" className="h-10 object-contain" />
                </div>
              )}
            </div>
            <div>
              <label className="form-label">Dashboard Banner</label>
              <input
                type="file"
                accept="image/*"
                className="form-input"
                onChange={handleBannerChange}
              />
              <p className="text-xs text-gray-400 mt-1">Recommended: 1200×300px</p>
              {bannerPreview && (
                <div className="mt-2 p-2 bg-gray-50 border rounded-lg">
                  <img src={bannerPreview} alt="Banner preview" className="h-16 w-full object-cover rounded" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Live Preview ── */}
        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-4">👁️ Live Preview</h3>
          <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
            {/* Header */}
            <div
              className="px-5 py-3 flex items-center gap-3"
              style={{ background: settings.header_color }}
            >
              <span className="text-white text-xl">🏛️</span>
              <span className="text-white font-bold text-sm">
                {settings.app_name || 'Government NOC System'}
              </span>
            </div>
            {/* Body */}
            <div className="p-6" style={{ background: settings.bg_color }}>
              <button
                type="button"
                className="px-5 py-2 text-white text-sm rounded-lg font-semibold"
                style={{ background: settings.primary_color }}
              >
                Primary Button
              </button>
              <p className="text-xs text-gray-500 mt-3">
                Background color: {settings.bg_color}
              </p>
            </div>
            {/* Footer */}
            <div
              className="px-5 py-2.5 text-xs"
              style={{ background: settings.footer_color, color: '#e2e8f0' }}
            >
              {settings.footer_text || '© 2024 Government NOC Management System'}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button type="submit" className="btn-primary px-8 py-3" disabled={saving}>
            {saving ? '⏳ Saving...' : '💾 Save Theme Settings'}
          </button>
          <button
            type="button"
            className="btn-secondary px-5 py-3"
            onClick={() => applyTheme(settings)}
          >
            👁️ Preview Now
          </button>
        </div>
      </form>
    </div>
  );
}